import { TimePicker } from "@tql/ui-components-react/kendo/dateinputs";
import type { FieldRenderProps } from "@tql/ui-components-react/kendo/form";
import { Field, FieldWrapper } from "@tql/ui-components-react/kendo/form";
/* eslint-disable eslint-comments/disable-enable-pair -- kendo skeleton */
/* eslint-disable @typescript-eslint/no-deprecated -- kendo skeleton */
import { Skeleton } from "@tql/ui-components-react/kendo/indicators";
import {
	Checkbox,
	Input,
	NumericTextBox,
	RadioGroup,
	TextArea,
} from "@tql/ui-components-react/kendo/inputs";
import { Error, Hint, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import {
	MaskedTextBox,
	TextBox,
} from "@tql/ui-components-react/tql/inputs/beta";
import type { CSSProperties } from "react";
import * as React from "react";

type ValidationMessage = string | false | null;

export enum FieldType {
	INPUT = "input",
	TEXTBOX = "textbox",
	DROPDOWN = "dropdown",
	TEXTAREA = "textarea",
	NUMERIC = "numeric",
	RADIOGROUP = "radiogroup",
	DATEPICKER = "datepicker",
	TIMEPICKER = "timepicker",
	MASKEDTEXTBOX = "maskedtextbox",
	CHECKBOX = "checkbox",
}

type ValidationInputFieldProps = {
	fieldType: FieldType;
	label?: React.ReactNode;
	id: string;
	name: string;
	disabled?: boolean;
	readOnly?: boolean;
	hint?: string;
	loading?: boolean;
	wrapperStyle?: CSSProperties;
} & Omit<FieldRenderProps, "component" | "name">;

export const FormInput = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		disabled,
		hint,
		type,
		optional,
		required,
		wrapperStyle,
		...others
	} = fieldRenderProps;

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && Boolean(hint);
	const hintId: string = showHint ? `${String(id)}_hint` : "";
	const errorId: string = showValidationMessage ? `${String(id)}_error` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorDisabled={Boolean(disabled)}
					editorId={String(id)}
					editorValid={Boolean(valid)}
					optional={Boolean(optional)}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}

			<div className={"k-form-field-wrap"}>
				<Input
					ariaDescribedBy={`${hintId} ${errorId}`}
					disabled={Boolean(disabled)}
					id={String(id)}
					type={String(type)}
					valid={Boolean(valid)}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormTextbox = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		disabled,
		hint,
		type,
		optional,
		required,
		wrapperStyle,
		...others
	} = fieldRenderProps;

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && Boolean(hint);
	const hintId: string = showHint ? `${String(id)}_hint` : "";
	const errorId: string = showValidationMessage ? `${String(id)}_error` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorDisabled={Boolean(disabled)}
					editorId={String(id)}
					editorValid={Boolean(valid)}
					optional={Boolean(optional)}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}

			<div className={"k-form-field-wrap"}>
				<TextBox
					disabled={Boolean(disabled)}
					id={String(id)}
					type={String(type)}
					valid={Boolean(valid)}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormDropDownList = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		disabled,
		hint,
		wrapperStyle,
		required,
		...others
	} = fieldRenderProps;
	const editorRef = React.useRef<HTMLElement>(null);

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && Boolean(hint);
	const hintId: string = showHint ? `${String(id)}_hint` : "";
	const errorId: string = showValidationMessage ? `${String(id)}_error` : "";
	const labelId: string = label ? `${String(id)}_label` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorDisabled={Boolean(disabled)}
					editorId={String(id)}
					editorRef={editorRef}
					editorValid={Boolean(valid)}
					id={labelId}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}

			<div className={"k-form-field-wrap"}>
				<DropDownList
					ref={editorRef}
					ariaDescribedBy={`${hintId} ${errorId}`}
					ariaLabelledBy={labelId}
					disabled={Boolean(disabled)}
					id={String(id)}
					valid={Boolean(valid)}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormTextArea = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		hint,
		disabled,
		optional,
		required,
		wrapperStyle,
		...others
	} = fieldRenderProps;

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && Boolean(hint);
	const hintId: string = showHint ? `${String(id)}_hint` : "";
	const errorId: string = showValidationMessage ? `${String(id)}_error` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorDisabled={Boolean(disabled)}
					editorId={String(id)}
					editorValid={Boolean(valid)}
					optional={Boolean(optional)}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}

			<div className={"k-form-field-wrap"}>
				<TextArea
					ariaDescribedBy={`${hintId} ${errorId}`}
					disabled={Boolean(disabled)}
					id={String(id)}
					valid={Boolean(valid)}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormNumericTextBox = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		disabled,
		hint,
		required,
		wrapperStyle,
		...others
	} = fieldRenderProps;

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && Boolean(hint);
	const hintId: string = showHint ? `${String(id)}_hint` : "";
	const errorId: string = showValidationMessage ? `${String(id)}_error` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorDisabled={Boolean(disabled)}
					editorId={String(id)}
					editorValid={Boolean(valid)}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}

			<div className={"k-form-field-wrap"}>
				<NumericTextBox
					ariaDescribedBy={`${hintId} ${errorId}`}
					disabled={Boolean(disabled)}
					id={String(id)}
					valid={Boolean(valid)}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormRadioGroup = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		id,
		label,
		valid,
		disabled,
		hint,
		wrapperStyle,
		required,
		...others
	} = fieldRenderProps;
	const editorRef = React.useRef(null);

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && Boolean(hint);
	const hintId: string = showHint ? `${String(id)}_hint` : "";
	const errorId: string = showValidationMessage ? `${String(id)}_error` : "";
	const labelId: string = label ? `${String(id)}_label` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorDisabled={Boolean(disabled)}
					editorId={String(id)}
					editorRef={editorRef}
					editorValid={Boolean(valid)}
					id={labelId}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}
			<div className={"k-form-field-wrap"}>
				<RadioGroup
					ref={editorRef}
					ariaDescribedBy={`${hintId} ${errorId}`}
					ariaLabelledBy={labelId}
					disabled={Boolean(disabled)}
					valid={Boolean(valid)}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormDatePicker = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		disabled,
		hint,
		wrapperStyle,
		hintDirection,
		required,
		...others
	} = fieldRenderProps;

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && Boolean(hint);
	const hintId: string = showHint ? `${String(id)}_hint` : "";
	const errorId: string = showValidationMessage ? `${String(id)}_error` : "";
	const labelId: string = label ? `${String(id)}_label` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorDisabled={Boolean(disabled)}
					editorId={String(id)}
					editorValid={valid}
					id={labelId}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}
			<div className={"k-form-field-wrap"}>
				<DatePicker
					ariaDescribedBy={`${hintId} ${errorId}`}
					ariaLabelledBy={labelId}
					disabled={Boolean(disabled)}
					id={String(id)}
					valid={valid}
					{...others}
				/>
				{showHint ? (
					<Hint
						direction={hintDirection as "start" | "end" | undefined}
						id={hintId}
					>
						{hint}
					</Hint>
				) : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormTimePicker = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		disabled,
		hint,
		wrapperStyle,
		required,
		...others
	} = fieldRenderProps;

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && (hint as boolean);
	const hintId: string = showHint ? `${id as string}_hint` : "";
	const errorId: string = showValidationMessage ? `${id as string}_error` : "";
	const labelId: string = label ? `${id as string}_label` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorDisabled={disabled as boolean}
					editorId={id as string}
					editorValid={valid}
					id={labelId}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}
			<div className={"k-form-field-wrap"}>
				<TimePicker
					ariaDescribedBy={`${hintId} ${errorId}`}
					ariaLabelledBy={labelId}
					disabled={disabled as boolean}
					id={id as string}
					valid={valid}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormMaskedTextBox = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		label,
		id,
		valid,
		hint,
		optional,
		required,
		wrapperStyle,
		...others
	} = fieldRenderProps;

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && (hint as boolean);
	const hintId: string = showHint ? `${id as string}_hint` : "";
	const errorId: string = showValidationMessage ? `${id as string}_error` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			{label ? (
				<Label
					className={"k-form-label"}
					editorId={id as string}
					editorValid={valid}
					optional={optional as boolean}
				>
					{label}
					{required ? (
						<>
							&nbsp;<span className={"required"}>*</span>
						</>
					) : null}
				</Label>
			) : null}
			<div className={"k-form-field-wrap"}>
				<MaskedTextBox
					ariaDescribedBy={`${hintId} ${errorId}`}
					id={id as string}
					valid={valid}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const FormCheckbox = (fieldRenderProps: FieldRenderProps) => {
	const {
		validationMessage,
		touched,
		id,
		valid,
		disabled,
		hint,
		wrapperStyle,
		...others
	} = fieldRenderProps;

	const showValidationMessage: ValidationMessage = touched && validationMessage;
	const showHint: boolean = !showValidationMessage && (hint as boolean);
	const hintId: string = showHint ? `${id as string}_hint` : "";
	const errorId: string = showValidationMessage ? `${id as string}_error` : "";

	return (
		<FieldWrapper style={wrapperStyle as CSSProperties}>
			<div className={"k-form-field-wrap"}>
				<Checkbox
					ariaDescribedBy={`${hintId} ${errorId}`}
					disabled={disabled as boolean}
					id={id as string}
					valid={valid}
					{...others}
				/>
				{showHint ? <Hint id={hintId}>{hint}</Hint> : null}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : null}
			</div>
		</FieldWrapper>
	);
};

export const ValidationInputField = (props: ValidationInputFieldProps) => {
	const { fieldType, loading, ...fieldRenderProps } = props;
	const hintId: string = fieldRenderProps["showHint"]
		? `${String(fieldRenderProps.id)}_hint`
		: "";

	if (loading) {
		return (
			<FieldWrapper style={fieldRenderProps.wrapperStyle}>
				{fieldRenderProps.label ? (
					<Label className={"k-form-label"} editorId={fieldRenderProps.id}>
						{fieldRenderProps.label}
						{fieldRenderProps["required"] ? (
							<>
								&nbsp;<span className={"required"}>*</span>
							</>
						) : null}
					</Label>
				) : null}
				<div className={"k-form-field-wrap"}>
					<Skeleton
						animation={{ type: "wave" }}
						shape={"rectangle"}
						style={{ width: "100%", height: 25.6, borderRadius: 3 }}
					/>
				</div>
				{fieldRenderProps["showHint"] ? (
					<Hint id={hintId}>{fieldRenderProps.hint}</Hint>
				) : null}
			</FieldWrapper>
		);
	}

	switch (fieldType) {
		case FieldType.INPUT: {
			return <Field component={FormInput} {...fieldRenderProps} />;
		}
		case FieldType.TEXTBOX: {
			return <Field component={FormTextbox} {...fieldRenderProps} />;
		}
		case FieldType.DROPDOWN: {
			return (
				<Field
					component={FormDropDownList}
					{...fieldRenderProps}
					wrapperStyle={{ width: "100%", ...fieldRenderProps.wrapperStyle }}
				/>
			);
		}
		case FieldType.TEXTAREA: {
			return <Field component={FormTextArea} {...fieldRenderProps} />;
		}
		case FieldType.NUMERIC: {
			return <Field component={FormNumericTextBox} {...fieldRenderProps} />;
		}
		case FieldType.RADIOGROUP: {
			return <Field component={FormRadioGroup} {...fieldRenderProps} />;
		}
		case FieldType.DATEPICKER: {
			return <Field component={FormDatePicker} {...fieldRenderProps} />;
		}
		case FieldType.TIMEPICKER: {
			return <Field component={FormTimePicker} {...fieldRenderProps} />;
		}
		case FieldType.MASKEDTEXTBOX: {
			return <Field component={FormMaskedTextBox} {...fieldRenderProps} />;
		}
		case FieldType.CHECKBOX: {
			return <Field component={FormCheckbox} {...fieldRenderProps} />;
		}
		default: {
			return null;
		}
	}
};
